import type {
    ZodType,
    ZodErrorMap,
    ZodOptional,
    ZodObject,
    ZodDefault,
    ZodNullable,
    ZodBoolean,
    ZodArray
} from 'zod'
import { core } from 'zod'
import { toValue } from 'vue'
import type { MaybeRefOrGetter } from 'vue'
import { type Composer } from 'vue-i18n'
import type { SupportedZodTypes } from '../types/form'

export function getZodSchemaShape(schema: ZodObject): core.$ZodShape {
    return schema.def.shape
}

export const isZodOptional = <T extends ZodOptional<any>>(type: SupportedZodTypes): type is T =>  type?.def?.type === 'optional'
export const isZodDefault = <T extends ZodDefault<any>>(type: SupportedZodTypes): type is T => type?.def?.type === 'default'
export const isZodObject = <T extends ZodObject<any>>(type: SupportedZodTypes): type is T => type?.def?.type === 'object'
export const isZodNullable = <T extends ZodNullable<any>>(type: SupportedZodTypes): type is T => type?.def?.type === 'nullable'
export const isZodBoolean = (type: SupportedZodTypes): type is ZodBoolean => type?.def?.type === 'boolean'
export const isZodArray = <T extends ZodArray<any>>(type: SupportedZodTypes): type is T => type?.def?.type === 'array'
export const isZodSimpleType = (type: unknown): type is ZodType =>
    !isZodObject(type as SupportedZodTypes)
    && typeof type === 'object' && type !== null
    && '_zod' in type

export function zRequiredIf<T extends ZodType | Record<string, ZodType>>(type: T, isRequired: MaybeRefOrGetter<boolean>): T extends ZodType ? ZodOptional<T> : { [K in keyof T]: T[K] extends ZodType ? ZodOptional<T[K]> : T[K] }
export function zRequiredIf<T extends ZodType | Record<string, ZodType>>(type: T, _isRequired: MaybeRefOrGetter<boolean>): T extends ZodType ? ZodOptional<T> : { [K in keyof T]: T[K] extends ZodType ? ZodOptional<T[K]> : T[K] } {
    const isRequired = toValue(_isRequired)

    // handle simple types
    if (isZodSimpleType(type)) {
        return (
            isRequired
                ? type
                : type.optional()
        ) as T extends ZodType ? ZodOptional<T> : never
    }

    // handle record
    const result: Partial<{ [K in keyof T]: T[K] extends ZodType ? ZodOptional<T[K]> : T[K] }> = {}

    for (const key in type) {
        const value = type[key]
        if (!isZodSimpleType(value)) {
            continue // Or handle error
        }
        result[key] = (isRequired ? value : value.optional()) as any    // TODO:  remove type cast
    }

    return result as T extends ZodType ? ZodOptional<T> : { [K in keyof T]: T[K] extends ZodType ? ZodOptional<T[K]> : T[K] }
}

export function createZodErrorMap(t: Composer['t']): ZodErrorMap {
    const parsedType = (data: any): string | undefined => {
        const type = typeof data

        switch (type) {
            case 'number': {
                return Number.isNaN(data) ? t('_core_simploshop.zod.types.nan') : t('_core_simploshop.zod.types.number')
            }
            case 'string': {
                return t('_core_simploshop.zod.types.string')
            }
            case 'boolean': {
                return t('_core_simploshop.zod.types.boolean')
            }
            case 'bigint': {
                return t('_core_simploshop.zod.types.bigint')
            }
            case 'function': {
                return t('_core_simploshop.zod.types.function')
            }
            case 'symbol': {
                return t('_core_simploshop.zod.types.symbol')
            }
            case 'undefined': {
                return undefined
            }
            case 'object': {
                if (Array.isArray(data)) {
                    return t('_core_simploshop.zod.types.array')
                }
                if (data === null) {
                    return t('_core_simploshop.zod.types.null')
                }

                if (Object.getPrototypeOf(data) !== Object.prototype && data.constructor) {
                    return data.constructor.name
                }
            }
        }
        return type
    }

    function getSizing(origin: string, value: number | bigint): string | null {
        return {
            string: t('_core_simploshop.zod.units.n_chars', [value.toString()], value as number),
            file: t('_core_simploshop.zod.units.n_bytes', [value.toString()], value as number),
            array: t('_core_simploshop.zod.units.n_items', [value.toString()], value as number),
            set: t('_core_simploshop.zod.units.n_items', [value.toString()], value as number),
        }[origin] ?? null
    }

    function getNoun(format: core.$ZodStringFormats | (string & {})): string | undefined {
        const mappings: Partial<Record<core.$ZodStringFormats | (string & {}), string>> = {
            regex: t('_core_simploshop.zod.types.regex'),
            email: t('_core_simploshop.zod.types.email'),
            url: t('_core_simploshop.zod.types.url'),
            emoji: t('_core_simploshop.zod.types.emoji'),
            uuid: t('_core_simploshop.zod.types.uuid'),
            uuidv4: t('_core_simploshop.zod.types.uuidv4'),
            uuidv6: t('_core_simploshop.zod.types.uuidv6'),
            nanoid: t('_core_simploshop.zod.types.nanoid'),
            guid: t('_core_simploshop.zod.types.guid'),
            cuid: t('_core_simploshop.zod.types.cuid'),
            cuid2: t('_core_simploshop.zod.types.cuid2'),
            ulid: t('_core_simploshop.zod.types.ulid'),
            xid: t('_core_simploshop.zod.types.xid'),
            ksuid: t('_core_simploshop.zod.types.ksuid'),
            datetime: t('_core_simploshop.zod.types.datetime'),
            date: t('_core_simploshop.zod.types.date'),
            time: t('_core_simploshop.zod.types.time'),
            duration: t('_core_simploshop.zod.types.duration'),
            ipv4: t('_core_simploshop.zod.types.ipv4'),
            ipv6: t('_core_simploshop.zod.types.ipv6'),
            cidrv4: t('_core_simploshop.zod.types.cidrv4'),
            cidrv6: t('_core_simploshop.zod.types.cidrv6'),
            base64: t('_core_simploshop.zod.types.base64'),
            base64url: t('_core_simploshop.zod.types.base64url'),
            json_string: t('_core_simploshop.zod.types.json_string'),
            e164: t('_core_simploshop.zod.types.e164'),
            jwt: t('_core_simploshop.zod.types.jwt'),
            template_literal: t('_core_simploshop.zod.types.template_literal'),
        }
        return mappings[format as keyof typeof mappings]
    }

    return (issue) => {
        switch (issue.code) {
            case 'invalid_type':
                const input = parsedType(issue.input)
                if (!input) {
                    return t('_core_simploshop.zod.field_required')
                }
                return t('_core_simploshop.zod.invalid_input_expected_received', [issue.expected, input])
            case 'invalid_value':
                if (issue.values.length === 1) return t('_core_simploshop.zod.invalid_input_expected', [core.util.stringifyPrimitive(issue.values[0])])
                return t('_core_simploshop.zod.invalid_option_one_of', [core.util.joinValues(issue.values, '|')])
            case 'too_big': {
                const adj = issue.inclusive ? '<=' : '<'
                const sizing = getSizing(issue.origin, issue.maximum)
                if (sizing) {
                    return t('_core_simploshop.zod.too_big_and_must_have', [parsedType(issue.origin) ?? t('_core_simploshop.zod.value'), `${adj} ${sizing}`])
                }
                return t('_core_simploshop.zod.too_big_and_must_be', [parsedType(issue.origin) ?? t('_core_simploshop.zod.value'), `${adj} ${issue.maximum.toString()}`])
            }
            case 'too_small': {
                const adj = issue.inclusive ? '>=' : '>'
                const sizing = getSizing(issue.origin, issue.minimum)
                if (sizing) {
                    return t('_core_simploshop.zod.too_small_and_must_have', [parsedType(issue.origin) ?? t('_core_simploshop.zod.value'), `${adj} ${sizing}`])
                }
                return t('_core_simploshop.zod.too_small_and_must_be', [parsedType(issue.origin) ?? t('_core_simploshop.zod.value'), `${adj} ${issue.minimum.toString()}`])
            }
            case 'invalid_format': {
                const _issue = issue as core.$ZodStringFormatIssues
                if (_issue.format === 'starts_with') return t('_core_simploshop.zod.invalid_string_starts_with', [_issue.prefix])
                if (_issue.format === 'ends_with') return t('_core_simploshop.zod.invalid_string_ends_with', [_issue.suffix])
                if (_issue.format === 'includes') return  t('_core_simploshop.zod.invalid_string_includes', [_issue.includes])
                if (_issue.format === 'regex') return t('_core_simploshop.zod.invalid_string_regex', [_issue.pattern])
                return t('_core_simploshop.zod.invalid_format', [getNoun(_issue.format) ?? issue.format])
            }
            case 'not_multiple_of':
                return t('_core_simploshop.zod.number_multiple', [issue.divisor])
            case 'unrecognized_keys':
                return t('_core_simploshop.zod.unknown_keys', [core.util.joinValues(issue.keys, ', ')])
            case 'invalid_key':
                return t('_core_simploshop.zod.invalid_key_in', [issue.origin])
            case 'invalid_union':
                return t('_core_simploshop.zod.invalid_input')
            case 'invalid_element':
                return t('_core_simploshop.zod.invalid_value_in', [issue.origin])
            default:
                return t('_core_simploshop.zod.invalid_input')
        }
    }
}
